import { Button, ModalContent } from "@kaltura/ds-react-components";
import { StyledContainer, StyledFormContainer, StyledPlayerContainer, StyledActions } from "./StyledComponents";
import { loadPlayerLib, translate } from "@kaltura/mediaspace-shared-utils";
import { useEffect, useState } from "react";
import { Player } from "@kaltura/mediaspace-shared-player";
import {
    getDefaultEmbedSetting,
    getDefaultEmbedSize,
    getDefaultPlayer,
    getEmbedSizesOptions,
    getPlayersOptions,
    getPlayerV7Config,
    getSelectedPlayerSettings,
} from "./helper";
import { EmbedCodeProps, EmbedSettings, PlayerConfig } from "./types";
import EmbedHelperV7, { EmbedSettingsAvailableFeatures } from "./embed-helpers/EmbedHelperV7";
import { LabelledValue, EntryType } from "@kaltura/mediaspace-shared-types";
import EmbedForm from "./forms/EmbedForm";
import OembedForm from "./forms/OembedForm";
import Oembed from "./embed-helpers/Oembed";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { baseUrl, serverUrl } from "@kaltura/mediaspace-shared-utils";

export interface SettingsLayoutProps {
    embedSettings: EmbedSettings[];
    availableFeatures?: EmbedSettingsAvailableFeatures;
    embedCodeParams: EmbedCodeProps;
    playerConfig: PlayerConfig;
    onClose: (analyticsButtonName?: string) => void;
    embedMethod: "embed" | "oembed";
    allowOembedConfig?: boolean;
    isTimingAllowed: boolean;
}

const SettingsLayout = (props: SettingsLayoutProps) => {
    const {
        embedSettings,
        availableFeatures,
        embedCodeParams,
        playerConfig,
        onClose,
        embedMethod,
        allowOembedConfig,
        isTimingAllowed,
    } = props;

    const enabledFeatures = {
        autoPlay: true,
        thumbnailEmbed: true,
        timeSelect: isTimingAllowed,
        embedSizes: true,
        embedCodeText: false,
        setAsTypeDefault: true,
        ...availableFeatures,
    };

    const v7Players = embedSettings.filter((player) => player.isPlayerV7);
    const defaultEmbedSetting = getDefaultEmbedSetting(v7Players);
    const playersOptions = getPlayersOptions(v7Players);

    const [embedCode, setEmbedCode] = useState<string>("");
    const [selectedPlayer, setSelectedPlayer] = useState<LabelledValue>(getDefaultPlayer(defaultEmbedSetting));
    const [mediaType, setMediaType] = useState<EntryType>(defaultEmbedSetting.entryType);
    const [v7LibLoaded, setV7LibLoaded] = useState<boolean>(false);
    const [playerV7Config, setPlayerV7Config] = useState(
        getPlayerV7Config(embedSettings, embedCodeParams, selectedPlayer, mediaType, playerConfig)
    );
    const [embedSizesOptions, setEmbedSizesOptions] = useState<LabelledValue[]>(
        getEmbedSizesOptions(defaultEmbedSetting.embedSizes)
    );
    const [selectedEmbedSize, setSelectedEmbedSize] = useState<LabelledValue>(getDefaultEmbedSize(defaultEmbedSetting));
    const [startTime, setStartTime] = useState<number | undefined>();

    /**
     * load player lib, then update state to cause re-render
     */
    const loadV7Lib = (libUrl: string) => {
        // setTimeout to allow a previous player instance to destroy
        // itself before removing the old library from the page
        window.setTimeout(
            () => {
                loadPlayerLib(libUrl, () => setV7LibLoaded(true));
            },
            100
        );
    };

    const { showToast } = useToastsContext();

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = (buttonName: string, buttonType: number) => {
        sendButtonAnalytics(buttonName, buttonType);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(embedCode);
        showToast({ severity: "success", message: translate("Link copied to clipboard.") });
        // send analytics
        const buttonName = `${embedMethod === "embed" ? "Embed" : "oEmbed"} - Copy embed code`;
        sendAnalytics(buttonName, ButtonClickAnalyticsType.SHARE);
    };
    const handlePlayerSelect = (selectedPlayer: LabelledValue) => {
        const settings = getSelectedPlayerSettings(embedSettings, selectedPlayer.value, selectedPlayer.label);
        setMediaType(settings.entryType);
        setSelectedPlayer(selectedPlayer);
        setV7LibLoaded(false);
        // send analytics
        const buttonName = `${embedMethod === "embed" ? "Embed" : "oEmbed"} - Choose player`;
        sendAnalytics(buttonName, ButtonClickAnalyticsType.CHOOSE);
    };
    const handleEmbedSizeSelect = (selectedEmbedSize: LabelledValue) => {
        setSelectedEmbedSize(selectedEmbedSize);
        // send analytics
        const buttonName = `${embedMethod === "embed" ? "Embed" : "oEmbed"} - Choose size`;
        sendAnalytics(buttonName, ButtonClickAnalyticsType.CHOOSE);
    };

    const onTimeUpdate = (startTime: number) => {
        setStartTime(startTime);
    };
    const oembedHost = serverUrl + (baseUrl ? `/${baseUrl}` : "");
    useEffect(() => {
        const settings = getSelectedPlayerSettings(embedSettings, selectedPlayer.value, selectedPlayer.label);
        // set embed sizes options according to selected player
        setEmbedSizesOptions(getEmbedSizesOptions(settings.embedSizes));

        if (!enabledFeatures.embedCodeText) {
            return;
        }

        const embedCode =
            embedMethod === "embed"
                ? EmbedHelperV7.generateEmbedCodeV7(
                      settings.playerId,
                      selectedEmbedSize.label,
                      mediaType,
                      embedCodeParams,
                      playerV7Config,
                      startTime
                  )
                : Oembed.generateOembedUrl(
                      oembedHost,
                      playerV7Config,
                      selectedEmbedSize.label,
                      settings.playerId,
                      startTime ?? 0
                  );
        setEmbedCode(embedCode);
    }, [selectedPlayer, selectedEmbedSize, startTime]);

    useEffect(() => {
        // set first embed size option as selected embed size when selecting different player
        const settings = getSelectedPlayerSettings(embedSettings, selectedPlayer.value, selectedPlayer.label);
        setSelectedEmbedSize(getEmbedSizesOptions(settings.embedSizes)[0]);
        setPlayerV7Config(getPlayerV7Config(embedSettings, embedCodeParams, selectedPlayer, mediaType, playerConfig));

        const { libUrl } = settings;
        loadV7Lib(libUrl);
    }, [selectedPlayer]);

    const className = "kms-ds-share-modal-settings";

    return (
        <div className={className}>
            <ModalContent>
                <StyledContainer>
                    <StyledFormContainer className={`${className}-form`}>
                        {embedMethod === "embed" && (
                            <EmbedForm
                                enabledFeatures={enabledFeatures}
                                embedCode={embedCode}
                                selectedPlayer={selectedPlayer}
                                playersOptions={playersOptions}
                                handlePlayerSelect={handlePlayerSelect}
                                embedSizesOptions={embedSizesOptions}
                                selectedEmbedSize={selectedEmbedSize}
                                handleEmbedSizeSelect={handleEmbedSizeSelect}
                                onTimeUpdate={onTimeUpdate}
                                embedMethod={embedMethod}
                            />
                        )}

                        {embedMethod === "oembed" && (
                            <OembedForm
                                enabledFeatures={enabledFeatures}
                                embedCode={embedCode}
                                selectedPlayer={selectedPlayer}
                                playersOptions={playersOptions}
                                handlePlayerSelect={handlePlayerSelect}
                                embedSizesOptions={embedSizesOptions}
                                selectedEmbedSize={selectedEmbedSize}
                                handleEmbedSizeSelect={handleEmbedSizeSelect}
                                onTimeUpdate={onTimeUpdate}
                                allowOembedConfig={allowOembedConfig ?? false}
                                embedMethod={embedMethod}
                            />
                        )}
                    </StyledFormContainer>
                    <StyledPlayerContainer>
                        {v7LibLoaded && (
                            <Player
                                config={playerV7Config}
                                media={{ entryId: playerV7Config.entry_id, playlistId: playerV7Config.playlist_id }}
                            />
                        )}
                    </StyledPlayerContainer>
                </StyledContainer>
            </ModalContent>

            <StyledActions className={className + "-actions"}>
                <Button
                    onClick={() => onClose(embedMethod === "embed" ? "Embed" : "oEmbed")}
                    variant={"borderless"}
                    color={"secondary"}
                >
                    {translate("Close")}
                </Button>
                <Button onClick={handleCopyLink}>{translate("Copy embed")}</Button>
            </StyledActions>
        </div>
    );
};

export default SettingsLayout;
